import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_URL } from 'src/assets/http/apis';
import { IApiResponse, IAuthResponse } from 'src/app/core/interfaces';
import { AuthService } from 'src/app/core/services/auth.service';
import { TokenStorageService } from 'src/app/core/services/token-storage.service';
import {
  IAcceptInvitationRequest,
  IInvitationDetails,
  IInvitationLookupRequest,
} from '../interfaces/invitations.interface';
import { IInvitationActionResponse } from '../interfaces/members.interface';

/**
 * Workspace-invitation API client (HK-174 / IVDR-260).
 *
 * The lookup and accept endpoints are anonymous — they are reached from the
 * `/accept-invite` screen before the invitee has a session — while revoke and
 * resend require an editor-or-above session and are called from the workspace
 * member list.
 */
@Injectable({ providedIn: 'root' })
export class InvitationService {
  constructor(
    private readonly http: HttpClient,
    private readonly tokenStorage: TokenStorageService,
    private readonly authService: AuthService
  ) {}

  /**
   * @description Resolves an invitation token into the details needed to render
   * the accept screen. A POST, not a GET: the token goes in the body so it never
   * reaches server access logs, App Insights, referrers or browser history —
   * putting it in the path would recreate defect F10. The token is never
   * persisted; it lives in the URL of the accept screen and in memory only.
   * @param {string} token - Raw token from the invitation email link.
   * @returns {Observable<IApiResponse<IInvitationDetails>>} The invitation details.
   */
  getInvitationByToken(token: string): Observable<IApiResponse<IInvitationDetails>> {
    const url = `${environment.apiUrl}${API_URL.INVITATIONS.LOOKUP}`;
    const body: IInvitationLookupRequest = { token };
    return this.http.post<IApiResponse<IInvitationDetails>>(url, body);
  }

  /**
   * @description Accepts an invitation: creates or activates the invitee inside
   * the inviting workspace and returns a session, so no verification code is
   * needed — the token itself proves ownership of the mailbox.
   * @param {IAcceptInvitationRequest} request - Token, optional password and profile answers.
   * @returns {Observable<IApiResponse<IAuthResponse>>} The issued session.
   */
  acceptInvitation(request: IAcceptInvitationRequest): Observable<IApiResponse<IAuthResponse>> {
    const url = `${environment.apiUrl}${API_URL.INVITATIONS.ACCEPT}`;
    return this.http.post<IApiResponse<IAuthResponse>>(url, request).pipe(
      tap(response => {
        const session = response?.data;
        if (!isCompleteSession(session)) {
          // All-or-nothing: never write a partial session and never flip the
          // app into an authenticated state off one. The response itself is
          // left alone (still a success envelope) so the caller decides what
          // the invitee sees — see the note on isCompleteSession.
          if (session) {
            console.warn('[InvitationService] Accept returned an incomplete session; not persisting it.');
          }
          return;
        }

        this.tokenStorage.setAuthSession({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          accessTokenExpiresAt: session.accessTokenExpiresAt,
          refreshTokenExpiresAt: session.refreshTokenExpiresAt,
        });
        this.authService.setIsAuthenticated(true);
      })
    );
  }

  /**
   * @description Revokes an outstanding invitation. Idempotent on the server.
   * @param {string} invitationId - The invitation to revoke.
   * @returns {Observable<IInvitationActionResponse>} The API response.
   */
  revokeInvitation(invitationId: string): Observable<IInvitationActionResponse> {
    const url = `${environment.apiUrl}${API_URL.INVITATIONS.REVOKE.replace('{id}', invitationId)}`;
    return this.http.delete<IInvitationActionResponse>(url);
  }

  /**
   * @description Re-sends an invitation email with a fresh token, invalidating
   * the previous one.
   * @param {string} invitationId - The invitation to resend.
   * @returns {Observable<IInvitationActionResponse>} The API response.
   */
  resendInvitation(invitationId: string): Observable<IInvitationActionResponse> {
    const url = `${environment.apiUrl}${API_URL.INVITATIONS.RESEND.replace('{id}', invitationId)}`;
    return this.http.post<IInvitationActionResponse>(url, {});
  }
}

/**
 * @description Whether a success envelope carries a session that is safe to
 * persist. All four fields are required, not just the two tokens.
 *
 * `TokenStorageService.setAuthSession` passes each field straight to
 * `localStorage.setItem`, which coerces its value to a string — so a missing
 * expiry is not stored as an absent key but as the literal text `'undefined'`.
 * That is a truthy string, so it slips past the `!accessTokenExpiresAt` guard
 * `TokenStorageService.readSession` uses to reject partial sessions, and it is
 * mirrored into every other tab by the BroadcastChannel sync — which then
 * reports `isAuthenticated = true` off a session that service's own validator
 * would have refused. Read as a date it is worse than absent:
 * `Date.parse('undefined')` is `NaN`, and every comparison against `NaN` is
 * false, so an expiry check fails open ("not expired") rather than closed.
 *
 * @param {IAuthResponse | null | undefined} session - The envelope's `data`.
 * @returns {boolean} True when all four session fields are present.
 */
function isCompleteSession(session: IAuthResponse | null | undefined): session is IAuthResponse {
  return !!(
    session?.accessToken &&
    session?.refreshToken &&
    session?.accessTokenExpiresAt &&
    session?.refreshTokenExpiresAt
  );
}
